import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { 
  CreditCard, 
  Truck, 
  MapPin, 
  User, 
  ArrowLeft,
  Lock,
  AlertCircle,
  Plus,
  Edit,
  Check,
  Home,
  Building,
  MapPin as LocationIcon,
  LogIn
} from 'lucide-react';
import api, { getImageUrl } from '../utils/api';
import RazorpayPayment from '../components/RazorpayPayment';
import TEXT_CONSTANTS from '../constants/text';
import { MetaPixelService } from '../services/metaPixel';

interface ShippingAddress {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
}

interface GuestInfo {
  name: string;
  email: string;
  phone: string;
}

interface SavedAddress {
  _id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  type: 'home' | 'work' | 'other';
  isDefault: boolean;
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { items, total, itemCount, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [isBuyNow, setIsBuyNow] = useState(false);
  const [buyNowItem, setBuyNowItem] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<'RAZORPAY' | 'COD'>('RAZORPAY');

  // Track payment method selection
  const handlePaymentMethodChange = (method: 'RAZORPAY' | 'COD') => {
    setPaymentMethod(method);
    
    MetaPixelService.trackCustomEvent('SelectPaymentMethod', {
      payment_method: method,
      value: getCurrentTotal(),
      currency: 'INR'
    });
  };
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [saveAddress, setSaveAddress] = useState(true);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [currentAddressType, setCurrentAddressType] = useState<'home' | 'work' | 'other'>('home');
  const [showRazorpayPayment, setShowRazorpayPayment] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [shippingCost, setShippingCost] = useState(0);
  const [codCharges, setCodCharges] = useState(0);
  const [isFreeShipping, setIsFreeShipping] = useState(false);
  
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });

  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    // Check if this is a Buy Now checkout
    const urlParams = new URLSearchParams(window.location.search);
    const isBuyNowCheckout = urlParams.get('buyNow') === 'true';
    
    if (isBuyNowCheckout) {
      const storedBuyNowItem = sessionStorage.getItem('buyNowItem');
      
      if (storedBuyNowItem) {
        try {
          const parsedItem = JSON.parse(storedBuyNowItem);
          setIsBuyNow(true);
          setBuyNowItem(parsedItem);
        } catch (error) {
          console.error('Error parsing buyNowItem:', error);
          navigate('/products');
          return;
        }
      } else {
        navigate('/products');
        return;
      }
    } else {
      // Regular cart checkout
      if (items.length === 0) {
        navigate('/cart');
        return;
      }
    }
    
    if (isAuthenticated) {
      fetchSavedAddresses();
    }
  }, [items, navigate, isAuthenticated]);

  // Calculate shipping when relevant data changes
  useEffect(() => {
    const currentTotal = getCurrentTotal();
    if (currentTotal > 0) {
      calculateShipping();
      
      // Track initiate checkout event
      const currentItems = getCurrentItems();
      MetaPixelService.trackInitiateCheckout(
        currentTotal,
        'INR',
        currentItems.length
      );
    }
  }, [paymentMethod, shippingAddress.state, total, buyNowItem]);

  // Helper functions to get current items and totals
  const getCurrentItems = () => {
    return isBuyNow && buyNowItem ? [buyNowItem] : items;
  };

  const getCurrentTotal = () => {
    if (isBuyNow && buyNowItem) {
      return buyNowItem.price * buyNowItem.quantity;
    }
    return total;
  };

  const getCurrentItemCount = () => {
    if (isBuyNow && buyNowItem) {
      return buyNowItem.quantity;
    }
    return itemCount;
  };

  const calculateShipping = async () => {
    try {
      const cartTotal = getCurrentTotal();
      const response = await api.post('/shipping/calculate', {
        cartTotal,
        shippingAddress,
        paymentMethod,
        shippingType: 'standard'
      });

      setShippingCost(response.data.shippingCost);
      setCodCharges(response.data.codCharges);
      setIsFreeShipping(response.data.isFreeShipping);
    } catch (error) {
      console.error('Error calculating shipping:', error);
      // Set default values on error
      setShippingCost(50);
      setCodCharges(paymentMethod === 'COD' ? 25 : 0);
      setIsFreeShipping(false);
    }
  };

  const getFinalTotal = () => {
    return getCurrentTotal() + shippingCost + codCharges;
  };

  const fetchSavedAddresses = async () => {
    try {
      const response = await api.get('/addresses');
      setSavedAddresses(response.data.addresses);
      
      // Auto-select default address
      const defaultAddress = response.data.addresses.find((addr: SavedAddress) => addr.isDefault);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress._id);
        loadAddressToForm(defaultAddress);
      } else if (response.data.addresses.length > 0) {
        // If no default, show address selection
        setShowAddressForm(false);
      } else {
        // No saved addresses, show form
        setShowAddressForm(true);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const loadAddressToForm = (address: SavedAddress) => {
    setShippingAddress({
      fullName: address.name,
      email: user?.email || '',
      phone: address.phone,
      address: address.street,
      city: address.city,
      state: address.state,
      pincode: address.zipCode,
      country: address.country
    });
    setCurrentAddressType(address.type);
  };

  const handleSaveAddress = async () => {
    console.log('Saving address with data:', shippingAddress);
    
    if (!validateForm()) {
      return;
    }

    try {
      const addressData = {
        name: shippingAddress.fullName,
        street: shippingAddress.address,
        city: shippingAddress.city,
        state: shippingAddress.state,
        zipCode: shippingAddress.pincode,
        country: shippingAddress.country,
        phone: shippingAddress.phone,
        type: currentAddressType,
        isDefault: savedAddresses.length === 0
      };

      console.log('Address data being sent:', addressData);

      let response: any;
      if (isEditingAddress && selectedAddressId) {
        console.log('Updating address:', selectedAddressId);
        response = await api.put(`/addresses/${selectedAddressId}`, addressData);
        // Update the saved addresses list
        setSavedAddresses(prevAddresses => 
          prevAddresses.map(addr => 
            addr._id === selectedAddressId 
              ? { ...response.data.address }
              : addr
          )
        );
      } else {
        console.log('Creating new address');
        response = await api.post('/addresses', addressData);
        console.log('Create address response:', response.data);
        setSavedAddresses(prevAddresses => [...prevAddresses, response.data.address]);
        setSelectedAddressId(response.data.address._id);
      }

      setShowAddressForm(false);
      setIsEditingAddress(false);
    } catch (error: any) {
      console.error('Error saving address:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Failed to save address. ';
      
      if (error.response?.data?.errors) {
        // Handle validation errors from express-validator
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map((err: any) => {
          // Make error messages more user-friendly
          switch (err.path) {
            case 'zipCode':
              return 'Pincode must be exactly 6 digits';
            case 'phone':
              return 'Phone number must be exactly 10 digits';
            case 'name':
              return 'Full name is required';
            case 'street':
              return 'Street address is required';
            case 'city':
              return 'City is required';
            case 'state':
              return 'State is required';
            default:
              return err.msg;
          }
        });
        errorMessage += errorMessages.join('. ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Please log in to save address';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      setError(errorMessage);
    }
  };

  const handleAddressChange = (field: keyof ShippingAddress, value: string) => {
    setShippingAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleGuestInfoChange = (field: keyof GuestInfo, value: string) => {
    setGuestInfo(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    setError(''); // Clear previous errors
    setFieldErrors({}); // Clear previous field errors
    
    const newFieldErrors: {[key: string]: string} = {};
    
    if (!isAuthenticated) {
      // Validate guest name
      if (!guestInfo.name.trim()) {
        newFieldErrors.guestName = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
      }
      
      // Validate guest email
      if (!guestInfo.email.trim()) {
        newFieldErrors.guestEmail = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
      } else if (!/\S+@\S+\.\S+/.test(guestInfo.email)) {
        newFieldErrors.guestEmail = TEXT_CONSTANTS.VALIDATION.INVALID_EMAIL;
      }
      
      // Validate guest phone
      if (!guestInfo.phone.trim()) {
        newFieldErrors.guestPhone = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
      } else if (!/^\d{10}$/.test(guestInfo.phone)) {
        newFieldErrors.guestPhone = TEXT_CONSTANTS.VALIDATION.INVALID_PHONE;
      }
      
      // If there are guest info errors, set general error too
      if (newFieldErrors.guestName || newFieldErrors.guestEmail || newFieldErrors.guestPhone) {
        const missing = [];
        if (!guestInfo.name) missing.push('name');
        if (!guestInfo.email) missing.push('email'); 
        if (!guestInfo.phone) missing.push('phone');
        setError(`Please fill in the following guest information: ${missing.join(', ')}`);
      }
    }

    // Check for required address fields
    if (!shippingAddress.fullName.trim()) {
      newFieldErrors.addressFullName = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
    }
    if (!shippingAddress.phone.trim()) {
      newFieldErrors.addressPhone = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
    } else if (!/^\d{10}$/.test(shippingAddress.phone)) {
      newFieldErrors.addressPhone = TEXT_CONSTANTS.VALIDATION.INVALID_PHONE;
    }
    if (!shippingAddress.address.trim()) {
      newFieldErrors.addressStreet = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
    }
    if (!shippingAddress.city.trim()) {
      newFieldErrors.addressCity = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
    }
    if (!shippingAddress.state.trim()) {
      newFieldErrors.addressState = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
    }
    if (!shippingAddress.pincode.trim()) {
      newFieldErrors.addressPincode = TEXT_CONSTANTS.VALIDATION.REQUIRED_FIELD;
    } else if (!/^\d{6}$/.test(shippingAddress.pincode)) {
      newFieldErrors.addressPincode = TEXT_CONSTANTS.VALIDATION.INVALID_ZIP;
    }
    
    // Set field errors
    setFieldErrors(newFieldErrors);
    
    // If there are any field errors, set appropriate general error messages and return false
    if (Object.keys(newFieldErrors).length > 0) {
      // Set general error message for address fields if needed
      const missingAddressFields = [];
      if (newFieldErrors.addressFullName) missingAddressFields.push('Full Name');
      if (newFieldErrors.addressPhone) missingAddressFields.push('Phone Number');
      if (newFieldErrors.addressStreet) missingAddressFields.push('Address');
      if (newFieldErrors.addressCity) missingAddressFields.push('City');
      if (newFieldErrors.addressState) missingAddressFields.push('State');
      if (newFieldErrors.addressPincode) missingAddressFields.push('Pincode');
      
      if (missingAddressFields.length > 0 && !error) {
        setError(`Please fill in the following address fields: ${missingAddressFields.join(', ')}`);
      }
      
      return false;
    }

    return true;
  };

  const handlePlaceOrder = async () => {
    setError('');
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Get current items (either cart or buyNow)
      const currentItems = getCurrentItems();
      
      // Validate items before creating order
      if (!currentItems || currentItems.length === 0) {
        setError(isBuyNow ? 'Buy now item is missing' : 'Your cart is empty');
        return;
      }

      // Check if all items have valid product data
      const invalidItems = currentItems.filter(item => !item.product || !item.product._id);
      if (invalidItems.length > 0) {
        setError('Some items are invalid. Please refresh and try again.');
        return;
      }

      const orderData = {
        items: currentItems.map(item => ({
          productId: item.product._id,
          quantity: item.quantity,
          size: item.size || '',
          color: item.color || ''
        })),
        shippingAddress: {
          name: shippingAddress.fullName,
          street: shippingAddress.address,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipCode: shippingAddress.pincode,
          country: shippingAddress.country,
          phone: shippingAddress.phone
        },
        paymentMethod,
        saveAddress: isAuthenticated && saveAddress,
        ...((!isAuthenticated) && { guestInfo })
      };

      console.log('Creating order with data:', orderData);
      const response = await api.post('/orders/create', orderData);
      
      if (paymentMethod === 'RAZORPAY') {
        // Show Razorpay payment interface
        setCurrentOrderId(response.data.order._id);
        setShowRazorpayPayment(true);
      } else {
        // COD order placed successfully
        if (isBuyNow) {
          sessionStorage.removeItem('buyNowItem');
        } else {
          clearCart();
        }
        navigate(`/order-success/${response.data.order._id}`);
      }
    } catch (error: any) {
      console.error('Error placing order:', error);
      
      if (error.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = error.response.data.errors.map((err: any) => 
          err.msg || err.message || 'Unknown validation error'
        ).join(', ');
        setError(`Please check: ${validationErrors}`);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(`Network error: ${error.message}`);
      } else {
        setError('Failed to place order. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };


  const handleRazorpaySuccess = (paymentDetails: any) => {
    if (isBuyNow) {
      sessionStorage.removeItem('buyNowItem');
    } else {
      clearCart();
    }
    navigate(`/order-success/${currentOrderId}?payment_id=${paymentDetails.paymentId}`);
  };

  const handleRazorpayError = (error: string) => {
    setError(error);
    setShowRazorpayPayment(false);
  };

  const handleBackFromRazorpay = () => {
    setShowRazorpayPayment(false);
  };

  if (items.length === 0 && !isBuyNow) {
    return null;
  }


  // Show Razorpay Payment Interface
  if (showRazorpayPayment && currentOrderId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <RazorpayPayment
            orderId={currentOrderId}
            amount={getFinalTotal() || 0}
            onSuccess={handleRazorpaySuccess}
            onError={handleRazorpayError}
            onBack={handleBackFromRazorpay}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/cart')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mr-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {TEXT_CONSTANTS.COMMON.BACK} to {TEXT_CONSTANTS.NAV.CART}
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {TEXT_CONSTANTS.CHECKOUT.CHECKOUT}
          </h1>
        </div>

        {/* Authentication Choice Banner */}
        {!isAuthenticated && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Choose how you'd like to checkout
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                  You can checkout as a guest or create an account for faster future orders.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Continuing as guest • Already have an account?
                  </span>
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    <LogIn className="h-4 w-4 mr-1" />
                    {TEXT_CONSTANTS.AUTH.SIGN_IN}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Forms */}
          <div className="lg:col-span-2 space-y-6">
            {/* Guest Information */}
            {!isAuthenticated && (
              <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Guest Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {TEXT_CONSTANTS.PROFILE.FULL_NAME} *
                    </label>
                    <input
                      type="text"
                      value={guestInfo.name}
                      onChange={(e) => handleGuestInfoChange('name', e.target.value)}
                      autoComplete="name"
                      className={
                        fieldErrors.guestName 
                          ? 'w-full px-4 py-4 border border-red-500 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 text-base min-h-[52px]'
                          : 'w-full px-4 py-4 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base min-h-[52px]'
                      }
                      placeholder={TEXT_CONSTANTS.PLACEHOLDERS.NAME}
                    />
                    {fieldErrors.guestName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.guestName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {TEXT_CONSTANTS.AUTH.EMAIL} *
                    </label>
                    <input
                      type="email"
                      value={guestInfo.email}
                      onChange={(e) => handleGuestInfoChange('email', e.target.value)}
                      inputMode="email"
                      autoComplete="email"
                      className={`w-full px-4 py-4 border rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 text-base min-h-[52px] ${
                        fieldErrors.guestEmail 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 dark:border-secondary-600 focus:ring-primary-500 focus:border-transparent'
                      }`}
                      placeholder={TEXT_CONSTANTS.PLACEHOLDERS.EMAIL}
                    />
                    {fieldErrors.guestEmail && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.guestEmail}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {TEXT_CONSTANTS.PROFILE.PHONE_NUMBER} *
                    </label>
                    <input
                      type="tel"
                      value={guestInfo.phone}
                      onChange={(e) => handleGuestInfoChange('phone', e.target.value)}
                      inputMode="numeric"
                      autoComplete="tel"
                      className={`w-full px-4 py-4 border rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 text-base min-h-[52px] ${
                        fieldErrors.guestPhone 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 dark:border-secondary-600 focus:ring-primary-500 focus:border-transparent'
                      }`}
                      placeholder={TEXT_CONSTANTS.PLACEHOLDERS.PHONE}
                      maxLength={10}
                    />
                    {fieldErrors.guestPhone && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.guestPhone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Shipping Address */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Shipping Address
                </h2>
                {isAuthenticated && savedAddresses.length > 0 && (
                  <button
                    onClick={() => setShowAddressForm(!showAddressForm)}
                    className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {showAddressForm ? 'Select Address' : 'Add New'}
                  </button>
                )}
              </div>

              {/* Saved Addresses Selection */}
              {isAuthenticated && savedAddresses.length > 0 && !showAddressForm && (
                <div className="space-y-3 mb-4">
                  {savedAddresses.map((address) => (
                    <div
                      key={address._id}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        selectedAddressId === address._id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-secondary-600 hover:border-gray-300 dark:hover:border-secondary-500'
                      }`}
                      onClick={() => {
                        setSelectedAddressId(address._id);
                        loadAddressToForm(address);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="flex items-center">
                              {address.type === 'home' && <Home className="h-4 w-4 text-green-600" />}
                              {address.type === 'work' && <Building className="h-4 w-4 text-blue-600" />}
                              {address.type === 'other' && <LocationIcon className="h-4 w-4 text-gray-600" />}
                              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                                {address.name}
                              </span>
                            </div>
                            {address.isDefault && (
                              <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                                Default
                              </span>
                            )}
                            {selectedAddressId === address._id && (
                              <Check className="h-4 w-4 text-primary-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {address.street}, {address.city}, {address.state} - {address.zipCode}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Phone: {address.phone}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAddressId(address._id);
                            loadAddressToForm(address);
                            setIsEditingAddress(true);
                            setShowAddressForm(true);
                          }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Address Form */}
              {(showAddressForm || (isAuthenticated && savedAddresses.length === 0) || !isAuthenticated) && (
                <div>
                  <div className="mb-4">
                    {isEditingAddress ? (
                      <h3 className="text-md font-medium text-gray-800 dark:text-gray-200">Edit Address</h3>
                    ) : (
                      <h3 className="text-md font-medium text-gray-800 dark:text-gray-200">Add New Address</h3>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {TEXT_CONSTANTS.PROFILE.FULL_NAME} *
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.fullName}
                    onChange={(e) => handleAddressChange('fullName', e.target.value)}
                    autoComplete="name"
                    className={
                      fieldErrors.addressFullName 
                        ? 'w-full px-4 py-4 border border-red-500 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 text-base min-h-[52px]'
                        : 'w-full px-4 py-4 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base min-h-[52px]'
                    }
                    placeholder={TEXT_CONSTANTS.PLACEHOLDERS.NAME}
                  />
                  {fieldErrors.addressFullName && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldErrors.addressFullName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {TEXT_CONSTANTS.PROFILE.PHONE_NUMBER} *
                  </label>
                  <input
                    type="tel"
                    value={shippingAddress.phone}
                    onChange={(e) => handleAddressChange('phone', e.target.value)}
                    inputMode="numeric"
                    autoComplete="tel"
                    className={
                      fieldErrors.addressPhone 
                        ? 'w-full px-4 py-4 border border-red-500 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 text-base min-h-[52px]'
                        : 'w-full px-4 py-4 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base min-h-[52px]'
                    }
                    placeholder={TEXT_CONSTANTS.PLACEHOLDERS.PHONE}
                    maxLength={10}
                    pattern="[0-9]{10}"
                  />
                  {fieldErrors.addressPhone && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldErrors.addressPhone}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {TEXT_CONSTANTS.PROFILE.STREET_ADDRESS} *
                  </label>
                  <textarea
                    value={shippingAddress.address}
                    onChange={(e) => handleAddressChange('address', e.target.value)}
                    rows={3}
                    autoComplete="street-address"
                    className={
                      fieldErrors.addressStreet 
                        ? 'w-full px-4 py-4 border border-red-500 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 text-base min-h-[88px] resize-none'
                        : 'w-full px-4 py-4 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base min-h-[88px] resize-none'
                    }
                    placeholder={TEXT_CONSTANTS.PLACEHOLDERS.ADDRESS}
                  />
                  {fieldErrors.addressStreet && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldErrors.addressStreet}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {TEXT_CONSTANTS.PROFILE.CITY} *
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    autoComplete="address-level2"
                    className={
                      fieldErrors.addressCity 
                        ? 'w-full px-4 py-4 border border-red-500 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 text-base min-h-[52px]'
                        : 'w-full px-4 py-4 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base min-h-[52px]'
                    }
                    placeholder={TEXT_CONSTANTS.PLACEHOLDERS.CITY}
                  />
                  {fieldErrors.addressCity && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldErrors.addressCity}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {TEXT_CONSTANTS.PROFILE.STATE} *
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.state}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                    autoComplete="address-level1"
                    className={
                      fieldErrors.addressState 
                        ? 'w-full px-4 py-4 border border-red-500 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 text-base min-h-[52px]'
                        : 'w-full px-4 py-4 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base min-h-[52px]'
                    }
                    placeholder={TEXT_CONSTANTS.PLACEHOLDERS.STATE}
                  />
                  {fieldErrors.addressState && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldErrors.addressState}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pincode *
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.pincode}
                    onChange={(e) => handleAddressChange('pincode', e.target.value)}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    className={
                      fieldErrors.addressPincode 
                        ? 'w-full px-4 py-4 border border-red-500 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 text-base min-h-[52px]'
                        : 'w-full px-4 py-4 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base min-h-[52px]'
                    }
                    placeholder="e.g. 110001 (6 digits only)"
                    maxLength={6}
                    pattern="[0-9]{6}"
                  />
                  {fieldErrors.addressPincode && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldErrors.addressPincode}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.country}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Address Type
                  </label>
                  <select
                    value={currentAddressType}
                    onChange={(e) => setCurrentAddressType(e.target.value as 'home' | 'work' | 'other')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="home">Home</option>
                    <option value="work">Work</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                {isAuthenticated && (
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={saveAddress}
                        onChange={(e) => setSaveAddress(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Save this address for future orders
                      </span>
                    </label>
                  </div>
                )}
                
                {(isEditingAddress || showAddressForm) && (
                  <div className="md:col-span-2 flex space-x-3">
                    <button
                      type="button"
                      onClick={handleSaveAddress}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {isEditingAddress ? 'Update Address' : 'Save Address'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddressForm(false);
                        setIsEditingAddress(false);
                        if (savedAddresses.length > 0) {
                          const defaultAddr = savedAddresses.find(addr => addr.isDefault) || savedAddresses[0];
                          setSelectedAddressId(defaultAddr._id);
                          loadAddressToForm(defaultAddr);
                        }
                      }}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Payment Method
              </h2>
              <div className="space-y-4">
                <label className={`relative flex items-center p-5 border-2 rounded-xl cursor-pointer transition-all min-h-[80px] ${
                  paymentMethod === 'RAZORPAY' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md' 
                    : 'border-gray-200 dark:border-secondary-600 hover:bg-gray-50 dark:hover:bg-secondary-700'
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="RAZORPAY"
                    checked={paymentMethod === 'RAZORPAY'}
                    onChange={(e) => handlePaymentMethodChange(e.target.value as 'RAZORPAY')}
                    className="sr-only"
                  />
                  
                  {/* Custom Radio Button - Centered */}
                  <div className={`w-6 h-6 rounded-full border-2 mr-4 flex items-center justify-center flex-shrink-0 ${
                    paymentMethod === 'RAZORPAY' 
                      ? 'border-primary-500 bg-primary-500' 
                      : 'border-gray-300 dark:border-secondary-500 bg-white dark:bg-secondary-700'
                  }`}>
                    {paymentMethod === 'RAZORPAY' && (
                      <div className="w-3 h-3 rounded-full bg-white"></div>
                    )}
                  </div>

                  {/* Payment Method Content */}
                  <div className="flex-1 pr-4">
                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20 mr-4 flex-shrink-0">
                        <CreditCard className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center font-semibold text-base text-gray-900 dark:text-gray-100">
                          <span>Card/UPI/Wallet</span>
                          <span className="ml-2 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs px-2 py-0.5 rounded-full font-medium">
                            Popular
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Secure payment via Razorpay</div>
                      </div>
                    </div>
                  </div>
                </label>
                
                <label className={`flex items-center p-5 border-2 rounded-xl cursor-pointer transition-all min-h-[80px] ${
                  paymentMethod === 'COD' 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md' 
                    : 'border-gray-200 dark:border-secondary-600 hover:bg-gray-50 dark:hover:bg-secondary-700'
                }`}>
                  <div className="flex items-center w-full">
                    <div className={`w-6 h-6 rounded-full border-2 mr-4 flex items-center justify-center ${
                      paymentMethod === 'COD' 
                        ? 'border-green-500 bg-green-500' 
                        : 'border-gray-300 bg-white'
                    }`}>
                      {paymentMethod === 'COD' && (
                        <div className="w-3 h-3 rounded-full bg-white"></div>
                      )}
                    </div>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="COD"
                      checked={paymentMethod === 'COD'}
                      onChange={(e) => handlePaymentMethodChange(e.target.value as 'COD')}
                      className="sr-only"
                    />
                    <div className="flex items-center flex-1">
                      <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20 mr-4">
                        <Truck className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-base text-gray-900 dark:text-gray-100">Cash on Delivery</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Pay when you receive your order (+₹25 fee)</div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Order Summary
              </h2>
              
              {/* Items */}
              <div className="space-y-3 mb-6">
                {getCurrentItems().map((item) => (
                  <div key={`${item.product._id}-${item.size}-${item.color}`} className="flex items-center space-x-3">
                    <img
                      src={getImageUrl(item.product.images[0])}
                      alt={item.product.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {item.product.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Qty: {item.quantity} × ₹{(item.product.discountedPrice || item.product.price).toLocaleString()}
                      </p>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      ₹{((item.product.discountedPrice || item.product.price) * item.quantity).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t border-gray-200 dark:border-secondary-600 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal ({getCurrentItemCount()} items)</span>
                  <span className="text-gray-900 dark:text-gray-100">₹{getCurrentTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Shipping</span>
                  {isFreeShipping ? (
                    <span className="text-green-600 dark:text-green-400">FREE</span>
                  ) : (
                    <span className="text-gray-900 dark:text-gray-100">₹{shippingCost.toLocaleString()}</span>
                  )}
                </div>
                {codCharges > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">COD Charges</span>
                    <span className="text-gray-900 dark:text-gray-100">₹{codCharges.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200 dark:border-secondary-600">
                  <span className="text-gray-900 dark:text-gray-100">Total</span>
                  <span className="text-primary-600 dark:text-primary-400">₹{getFinalTotal().toLocaleString()}</span>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mr-2" />
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                </div>
              )}

              {/* Place Order Button */}
              <button
                onClick={handlePlaceOrder}
                disabled={isLoading}
                className="w-full mt-6 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                         text-white py-4 px-6 rounded-xl text-lg font-semibold transition-colors shadow-lg
                         disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center min-h-[56px]
                         active:transform active:scale-[0.98] transition-transform"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3" />
                ) : (
                  <Lock className="h-5 w-5 mr-3" />
                )}
                {isLoading ? 'Processing Your Order...' : `Place Order - ₹${getFinalTotal().toLocaleString()}`}
              </button>

              <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
                🔒 Your payment information is secure and encrypted
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;